import { useState } from 'react';

import { SoftCard } from '@/components/ui/soft-card';
import { Toggle } from '@/components/ui/toggle';
import { useToast } from '@/components/ui/toast';
import { useChannelActions } from '@/hooks/useChannelActions';

/**
 * Per-channel multi-item basket toggle for the order-channel pair. Unlike the
 * notification cards (which twin-write both records so a retailer never enters
 * the same details twice), this deliberately writes ONLY the current channel's
 * `config.multiItemBasket` — a retailer may well want baskets on Local
 * Checkout but single-item Product Reservations, so the two flags stay
 * independent. Absent flag = off.
 *
 * The toggle is optimistic: flip immediately, revert + toast if the PATCH
 * fails.
 */
export function MultiItemBasketCard({ shopId, channelType, value }) {
	const { update } = useChannelActions(shopId);
	const { toast } = useToast();
	const [enabled, setEnabled] = useState(Boolean(value));

	const handleToggle = async (event) => {
		const next = event.target.checked;
		setEnabled(next);
		try {
			await update(channelType, { config: { multiItemBasket: next } });
		} catch (err) {
			setEnabled(!next);
			toast({
				title: 'Could not update multi-item baskets',
				description: err?.message ?? 'Please try again.',
				variant: 'destructive'
			});
		}
	};

	const description =
		channelType === 'local-checkout'
			? 'Let shoppers add several items to a basket before checking out.'
			: 'Let shoppers add several items to a basket before reserving.';

	return (
		<SoftCard className="space-y-5 !p-5 md:!p-6">
			<div>
				<h3 className="text-sm font-bold text-foreground">Multi-item baskets</h3>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</div>
			<Toggle label="Allow multi-item baskets" checked={enabled} onChange={handleToggle} data-testid="multi-item-basket-toggle" />
		</SoftCard>
	);
}
